// Reactive state container with subscribe/emit pattern.
// Replaces the legacy window.STATE object with explicit change notifications.

const listeners = new Map(); // key -> Set<callback>

function notify(key) {
  const subs = listeners.get(key);
  if (!subs) return;
  for (const cb of subs) {
    try { cb(store[key]); } catch (e) { console.error('[state] listener error', e); }
  }
  // wildcard listeners
  const wildcard = listeners.get('*');
  if (wildcard) for (const cb of wildcard) { try { cb(key, store[key]); } catch {} }
}

const store = {
  // Session
  familyId: null,
  uid: null,
  role: 'owner',           // 'owner' | 'maestro' | 'hero'
  selectedHeroId: null,
  isHeroMode: false,
  userProfile: null,

  // Family data (mirrors Firestore via listeners)
  meta: {},
  heroes: [],
  maestros: [],
  habits: [],
  rewards: [],
  approvals: [],
  history: [],
  calendarEvents: [],
  daily: {},               // { 'YYYY-MM-DD': dailyDoc }
  notifications: [],
  alertLogs: [],
  maestroNotifications: [],

  // Subscription/billing (FASE 2)
  subscription: { tier: 'free', status: 'active' },

  // Wellness (FASE 1/3.5)
  wellnessAlerts: [],

  // UI
  currentScreen: 'auth',
  syncStatus: 'idle',      // 'idle' | 'syncing' | 'offline'
};

export const state = new Proxy(store, {
  set(target, key, value) {
    const prev = target[key];
    target[key] = value;
    if (prev !== value) notify(key);
    return true;
  },
});

// Ergonomic accessor for session-related fields (used by paths.js)
export const session = new Proxy({}, {
  get(_, key) { return store[key]; },
  set(_, key, value) { state[key] = value; return true; },
});

export function subscribe(key, cb) {
  if (!listeners.has(key)) listeners.set(key, new Set());
  listeners.get(key).add(cb);
  return () => listeners.get(key)?.delete(cb);
}

export function setMany(patch) {
  for (const [k, v] of Object.entries(patch)) state[k] = v;
}

// Legacy bridge — expose window.STATE for old code paths during migration
if (typeof window !== 'undefined') {
  window.STATE = store;
  Object.defineProperties(window, {
    FAMILY_ID:        { get: () => store.familyId,       set: (v) => { state.familyId = v; } },
    SELECTED_HERO_ID: { get: () => store.selectedHeroId, set: (v) => { state.selectedHeroId = v; } },
    CURRENT_USER_UID: { get: () => store.uid,            set: (v) => { state.uid = v; } },
    isHeroMode:       { get: () => store.isHeroMode,     set: (v) => { state.isHeroMode = v; } },
  });
}
